import i18n from "../i18n";

const BASE_URL = "https://api.transcirox.com/api";
// const BASE_URL = "http://TransCirox.test/api";

function translateApiText(text) {
  if (typeof text !== "string") {
    return text;
  }

  const normalizedText = text.trim();

  if (!normalizedText) {
    return normalizedText;
  }

  return i18n.exists(normalizedText) ? i18n.t(normalizedText) : normalizedText;
}

function flattenApiMessages(value) {
  if (!value) {
    return [];
  }

  if (typeof value === "string") {
    return [translateApiText(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenApiMessages(item));
  }

  if (typeof value === "object") {
    return Object.values(value).flatMap((item) => flattenApiMessages(item));
  }

  return [];
}

function getDefaultApiErrorMessage(status) {
  if (status === 401) {
    return i18n.t("api_errors.unauthorized");
  }

  if (status === 403) {
    return i18n.t("api_errors.forbidden");
  }

  if (status >= 500) {
    return i18n.t("api_errors.server_error");
  }

  return i18n.t("api_errors.request_failed");
}

function normalizeApiError(error, status) {
  const resolvedStatus = status ?? error?.status ?? 0;
  const rawMessage = error?.message;
  const rawErrors =
    error?.errors ??
    error?.data ??
    (rawMessage && typeof rawMessage === "object" ? rawMessage : null);

  const errors = Array.from(
    new Set(flattenApiMessages(rawErrors).filter(Boolean)),
  );

  const message =
    typeof rawMessage === "string"
      ? translateApiText(rawMessage)
      : errors[0] || getDefaultApiErrorMessage(resolvedStatus);

  return {
    success: false,
    status: resolvedStatus,
    message,
    errors,
    raw: error,
  };
}

export function translateBackendMessage(value, fallback = "") {
  if (typeof value === "string") {
    return translateApiText(value) || fallback;
  }

  if (value && typeof value === "object") {
    return normalizeApiError(value).message || fallback;
  }

  return fallback;
}

async function apiSystem(endpoint, { body, ...customConfig } = {}) {
  const token =
    localStorage.getItem("token") || localStorage.getItem("temp_token");
  const lang = localStorage.getItem("i18nextLng") || "de";

  const supportedLangs = ["ar", "en", "de", "fr"];
  const selectedLang = supportedLangs.includes(lang) ? lang : "en";

  const headers = {
    Accept: "application/json",
    "Accept-Language": selectedLang,
  };

  if (body && !(body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const config = {
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
  };

  if (body) {
    config.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  console.log("Sending request to:", `${BASE_URL}/${endpoint}`, config);

  const response = await fetch(`${BASE_URL}/${endpoint}`, config);

  if (response.status === 403) {
    localStorage.removeItem("token");
    localStorage.removeItem("temp_token");
    localStorage.removeItem("user");
    localStorage.removeItem("userType");

    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
    return Promise.reject({
      status: 403,
      message: "Unauthorized",
    });
  }

  let data = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (response.ok) {
    return data;
  } else {
    return Promise.reject({
      ...(data || {}),
      status: response.status,
    });
  }
}

function buildQuery(params = {}) {
  const query = new URLSearchParams(params).toString();
  return query ? `?${query}` : "";
}

export const api = {
  get: (endpoint, { params, ...config } = {}) =>
    apiSystem(`${endpoint}${buildQuery(params)}`, { ...config, method: "GET" })
      .then((data) => ({ success: true, data }))
      .catch((error) => normalizeApiError(error)),

  post: (endpoint, body, config = {}) =>
    apiSystem(endpoint, { ...config, method: "POST", body })
      .then((data) => ({ success: true, data }))
      .catch((error) => normalizeApiError(error)),

  put: (endpoint, body, config = {}) =>
    apiSystem(endpoint, { ...config, method: "PUT", body })
      .then((data) => ({ success: true, data }))
      .catch((error) => normalizeApiError(error)),

  delete: (endpoint, config = {}) =>
    apiSystem(endpoint, { ...config, method: "DELETE" })
      .then((data) => ({ success: true, data }))
      .catch((error) => normalizeApiError(error)),
};

// Contact APIs
export const contactAPI = {
  store: (data) => api.post("contacts", data),
};
// City APIs
export const loadingCityApi = {
  index: (params = {}) => api.get("loading-city", { params }),
};
// Postal Area APIs
export const postalAreaAPI = {
  index: (params = {}) => api.get("postal-area", { params }),
  show: (id) => api.get(`postal-area/${id}`),
};
export const postalAreaAPI_New = {
  index: (params = {}) => api.get("postal-area-new", { params }),
  show: (id) => api.get(`postal-area-new/${id}`),
};

// Settings APIs
export const settingsAPI = {
  index: () => api.get("settings"),
};

// Socials APIs
export const socialsAPI = {
  index: () => api.get("socials"),
};

// Auth APIs
export const authAPI = {
  // User Registration & Authentication
  registerUser: (data) => api.post("customer/user-register", data),
  registerDriver: (data) => api.post("customer/driver-register", data),
  confirmAccount: (data) => api.post("customer/confirm-account", data),
  verifyOtp: (data) => api.post("customer/verify/otp", data),
  login: (data) => api.post("customer/login", data),
  getProfile: () => api.get("customer/get-profile"),
  changePassword: (data) => api.post("customer/change-password", data),
  logout: () => api.post("customer/logout"),
  forgotPassword: (data) => api.post("customer/forgot-password", data),
  resetPassword: (data) => api.post("customer/reset-password", data),
  deleteAccount: (data) => api.post("customer/delete-account", data),

  // Guest APIs
  guestLogin: (data) => api.post("guest/login", data),
  guestSetPassword: (data) => api.post("guest/set-password", data),

  // Vehicle APIs
  vehicleLogin: (data) => api.post("vehicle/login", data),
};

export const vehicleAPI = {
  orders: {
    active: (params = {}) => api.get("vehicle/orders/active", { params }),
    completed: (params = {}) => api.get("vehicle/orders/completed", { params }),
    complete: (id) => api.post(`vehicle/orders/${id}/complete`),
  },
  transportOrders: {
    receive: (id, data) =>
      api.post(`vehicle/transport-orders/${id}/receive`, data),
    complete: (id, data) =>
      api.post(`vehicle/transport-orders/${id}/complete`, data),
    transfer: (id, data) =>
      api.post(`vehicle/transport-orders/${id}/transfer`, data),
  },
  bookingOrders: {
    receive: (id, data) =>
      api.post(`vehicle/booking-orders/${id}/receive`, data),
    complete: (id, data) =>
      api.post(`vehicle/booking-orders/${id}/complete`, data),
    transfer: (id, data) =>
      api.post(`vehicle/booking-orders/${id}/transfer`, data),
  },
};

// Driver APIs
export const driverAPI = {
  // Chat APIs
  chat: {
    conversations: () => api.get("driver/chats/conversations"),
    send: (data) => api.post("driver/chats/send", data),
    markAsRead: () => api.post("driver/chats/mark-as-read"),
  },

  // Vehicles APIs
  vehicles: {
    index: (params = {}) => api.get("driver/vehicles", { params }),
    show: (id) => api.get(`driver/vehicles/${id}`),
    store: (data) => api.post("driver/vehicles", data),
    update: (id, data) => api.post(`driver/vehicles/${id}`, data),
    delete: (id) => api.delete(`driver/vehicles/${id}`),
  },

  // Vehicles APIs
  advertisements: {
    index: (params = {}) => api.get("driver/advertisements", { params }),
    show: (id) => api.get(`driver/advertisements/${id}`),
    categories: (params = {}) => api.get("driver/categories", { params }),
    byCategory: (categoryId) => api.get(`driver/advertisements/by-category/${categoryId}`),
  },

  // Booking Approved APIs
  bookings: {
    index: (params = {}) => {
      return api.get("driver/bookings", { params });
    },
    show: (id) => api.get(`driver/bookings/${id}`),
    regular: (params = {}) => {
      console.log("🚀 ~ params:", params);
      return api.get("driver/bookings/regular", { params });
    },
    allRegular: () => api.get("driver/bookings/all-regular"),
    regularShow: (id) => api.get(`driver/bookings/regular/${id}`),
    fast: (params = {}) => api.get("driver/bookings/fast", { params }),
    allFast: () => api.get("driver/bookings/all-fast"),
    fastShow: (id) => api.get(`driver/bookings/fast/${id}`),
  },

  // Booking Orders APIs
  bookingsOrders: {
    index: (params = {}) => api.get("driver/bookings-orders", { params }),
    show: (id) => api.get(`driver/bookings-orders/${id}`),
    regular: (params = {}) =>
      api.get("driver/bookings-orders/regular", { params }),
    calculatePercentage: (data) =>
      api.post("driver/bookings-orders/calculate-percentage", data),
    store: (data) => api.post("driver/bookings-orders", data),
    receive: (id, data) =>
      api.post(`driver/bookings-orders/${id}/receive`, data),
    complete: (id, data) =>
      api.post(`driver/bookings-orders/${id}/complete`, data),
    updateLocation: (id, data) =>
      api.post(`driver/bookings-orders/update-location/${id}`, data),

    pending: (params = {}) =>
      api.get("driver/bookings-orders/regular/pending", { params }),
    accepted: (params = {}) =>
      api.get("driver/bookings-orders/accepted", { params }),
    completed: (params = {}) =>
      api.get("driver/bookings-orders/completed", { params }),
    regularCompleted: (params = {}) =>
      api.get("driver/bookings-orders/regular/completed", { params }),

    // ✅ الجديد:
    regularAccepted: (params = {}) =>
      api.get("driver/bookings-orders/regular/accepted", { params }),

    delete: (id) => api.delete(`driver/bookings-orders/${id}`),
    transfer: (id, data) =>
      api.post(`driver/bookings-orders/${id}/transfer`, data),
  },

  // Live Track APIs
  bookingsOrdersLiveTrack: {
    show: (id) => api.get(`driver/bookings-orders/location/${id}`),
    updateLocation: (id, data) =>
      api.post(`driver/bookings-orders/update-location/${id}`, data),
  },

  // Fast Orders APIs
  fastOrders: {
    index: (params = {}) => api.get("driver/fast-orders", { params }),
    show: (id) => api.get(`driver/fast-orders/${id}`),
    store: (data) => api.post("driver/fast-orders", data),
    receive: (id, data) => api.post(`driver/fast-orders/${id}/receive`, data),
    complete: (id, data) => api.post(`driver/fast-orders/${id}/complete`, data),
    completed: (params = {}) =>
      api.get("driver/fast-orders/completed", { params }),

    // ✅ الجديد:
    accepted: (params = {}) =>
      api.get("driver/fast-orders/accepted", { params }),
  },

  // Transport Approved APIs
  transports: {
    index: (params = {}) => api.get("driver/transports", { params }),
    show: (id) => api.get(`driver/transports/${id}`),
    cars: (params = {}) => api.get("driver/transports/cars", { params }),
    allCars: () => api.get("driver/transports/all-cars"),
    carsShow: (id) => api.get(`driver/transports/cars/${id}`),
    furnitures: (params = {}) =>
      api.get("driver/transports/furnitures", { params }),
    allFurnitures: () => api.get("driver/transports/all-furnitures"),
    furnituresShow: (id) => api.get(`driver/transports/furnitures/${id}`),
    calculateProfit: (data) =>
      api.post("driver/transports/calculate-profit", data),
    startInquiry: (transportId) =>
      api.post(`driver/transports/${transportId}/inquiry`),
    getMyOrder: (transportId) => api.get(`driver/transports/${transportId}/orders`),
    store: (data) => api.post("driver/transports", data),
    rateCustomer: (transportId, orderId, data) =>
      api.post(`driver/transports/${transportId}/orders/${orderId}/ratings`,data),
    completedRated: (params = {}) =>
      api.get('driver/transports/completed/rated', { params }),
    completedUnrated: (params = {}) =>
      api.get('driver/transports/completed/unrated', { params }),
  },

  // Transport Orders APIs
  transportsOrders: {
    index: (params = {}) => api.get("driver/transports-orders", { params }),
    show: (id) => api.get(`driver/transports-orders/${id}`),
    cars: (params = {}) => api.get("driver/transports-orders/cars", { params }),
    carsShow: (id) => api.get(`driver/transports-orders/cars/${id}`),

    furnitures: (params = {}) =>
      api.get("driver/transports-orders/furnitures", { params }),
    furnituresShow: (id) =>
      api.get(`driver/transports-orders/furnitures/${id}`),

    invoices: (params = {}) =>
      api.get("driver/transports-orders/invoices", { params }),

    receive: (id, data) =>
      api.post(`driver/transports-orders/${id}/receive`, data),
    complete: (id, data) =>
      api.post(`driver/transports-orders/${id}/complete`, data),
    transfer: (id, data) =>
      api.post(`driver/transports-orders/${id}/transfer`, data),

    pending: (params = {}) =>
      api.get("driver/transports-orders/pending", { params }),
    accepted: (params = {}) =>
      api.get("driver/transports-orders/accepted", { params }),
    completed: (params = {}) =>
      api.get("driver/transports-orders/completed", { params }),

    // ✅ الجديد:
    carPending: (params = {}) =>
      api.get("driver/transports-orders/car/pending", { params }),
    furniturePending: (params = {}) =>
      api.get("driver/transports-orders/furniture/pending", { params }),

    // ✅ الجديد المضاف الآن (accepted):
    carAccepted: (params = {}) =>
      api.get("driver/transports-orders/car/accepted", { params }),
    furnitureAccepted: (params = {}) =>
      api.get("driver/transports-orders/furniture/accepted", { params }),

    carCompleted: (params = {}) =>
      api.get("driver/transports-orders/car/completed", { params }),
    furnitureCompleted: (params = {}) =>
      api.get("driver/transports-orders/furniture/completed", { params }),
    deleteCar: (id) => api.delete(`driver/transports-orders/${id}`),
    deleteFurniture: (id) => api.delete(`driver/transports-orders/${id}`),
    transportsChat: (params = {}) => api.get("driver/transports-orders/messages", { params }),
  },

  // Notification APIs
  notifications: {
    index: () => api.get("driver/notifications"),
    unreadCount: () => api.get("driver/notifications/unread-count"),
    markRead: (id) => api.post(`driver/notifications/${id}/mark-read`),
    markAllRead: () => api.post("driver/notifications/mark-all-read"),
  },

  // Transport Orders Live Track APIs
  transportsOrdersLiveTrack: {
    show: (id) => api.get(`driver/transports-orders/location/${id}`),
    updateLocation: (id, data) =>
      api.post(`driver/transports-orders/update-location/${id}`, data),
  },

  // Transport Order Chat APIs
  transportOrderChat: {
    getMessages: (transportId, orderId, params = {}) =>
      api.get(`driver/transports/${transportId}/orders/${orderId}/messages`, {
        params,
      }),
    sendMessage: (transportId, orderId, data) => {
      const cleanOrderId = parseInt(orderId?.toString().replace("No.", ""));
      return api.post(
        `driver/transports/${transportId}/orders/${cleanOrderId}/messages`,
        data,
      );
    },
    makeRead: (transportId, orderId, params = {}) =>
      api.post(
        `driver/transports/${transportId}/orders/${orderId}/messages/mark-read`,
        {
          params,
        },
      ),
  },

  // Count APIs
  counts: () => api.get("driver/counts"),

  // Profile APIs
  updateProfile: (data) => api.post("driver/update-profile", data),
  deleteDocumentFile: (fileId) => api.delete(`driver/documents/${fileId}`),
  savedFilters: {
    index: () => api.get("driver/saved-filters"),
    store: (data) => api.post("driver/saved-filters", data),
    delete: (id) => api.delete(`driver/saved-filters/${id}`),
  },
};

// Driver Company APIs
export const driverCompanyAPI = {
  // Chat APIs
  chat: {
    conversations: () => api.get("driver/chats/conversations"),
    send: (data) => api.post("driver/chats/send", data),
    markAsRead: () => api.post("driver/chats/mark-as-read"),
  },

  // Booking Orders APIs
  orders: {
    doneAll: (params = {}) => api.get("driver/orders/done-all", { params }),
    done: (params = {}) => api.get("driver/orders/done", { params }),
    index: (params = {}) => api.get("driver/orders", { params }),
    show: (id) => api.get(`driver/orders/${id}`),
    receive: (id, data) => api.post(`driver/orders/${id}/receive`, data),
    complete: (id, data) => api.post(`driver/done-order/${id}`, data),
    delete: (id) => api.delete(`driver/orders/${id}`),
  },

  // Special Booking Orders APIs
  specialBookings: {
    completed: (params = {}) =>
      api.get("driver/special-bookings-completed", { params }),
    completedShow: (id) => api.get(`driver/special-bookings-completed/${id}`),
    index: (params = {}) => api.get("driver/special-bookings", { params }),
    show: (id) => api.get(`driver/special-bookings/${id}`),
    receive: (id, data) =>
      api.post(`driver/special-bookings/${id}/receive`, data),
    complete: (id, data) =>
      api.post(`driver/special-bookings/${id}/complete`, data),
  },

  // Profile APIs
  updateProfile: (data) => api.post("driver/update-profile", data),
  deleteDocumentFile: (fileId) => api.delete(`driver/documents/${fileId}`),
};

// User APIs
export const userAPI = {
  coupons: {
    validate: (data) => api.post('coupons/validate', data),
  },

  // employee APIs
  employees: {
    index: (params = {}) => api.get("user/employees", { params }),
    show: (id) => api.get(`user/employees/${id}`),
    store: (data) => api.post("user/employees", data),
    update: (id, data) => api.post(`user/employees/${id}`, data),
    delete: (id, data) => api.post(`user/employees/${id}/delete`, data),
    toggleCreateOrders: (id, data) =>
      api.post(`user/employees/${id}/toggle-create-orders`, data),
    bookings: (employeeId, params = {}) =>
      api.get(`user/employees/${employeeId}/bookings`, { params }),
    showbooking: (employeeId, id) =>
      api.get(`user/employees/${employeeId}/bookings/${id}`),
    bookingTrack: (employeeId, bookingId) =>
      api.get(`user/employees/${employeeId}/bookings-track`, {
        params: { booking_id: bookingId },
      }),
    bookingLiveTrack: (employeeId, id) => api.get(`user/employees/${employeeId}/bookings-live-track/${id}`),
    cars: (employeeId, params = {}) =>
      api.get(`user/employees/${employeeId}/transports/cars`, { params }),
    furnitures: (employeeId, params = {}) =>
      api.get(`user/employees/${employeeId}/transports/furnitures`, { params }),
    showTransport: (employeeId, id) =>
      api.get(`user/employees/${employeeId}/transports/${id}`),
    showTransports: (employeeId, id) =>
      api.get(`user/employees/${employeeId}/transports/${id}`),
    byTransport: (employeeId, transportId, params = {}) => api.get(`user/employees/${employeeId}/transports/${transportId}/orders`, { params }),
    getMessages: (employeeId, transportId, orderId, params = {}) => api.get(`user/employees/${employeeId}/transports/${transportId}/orders/${orderId}/messages`, { params, }),
    transportTrack: (employeeId, transportId) =>
      api.get(`user/employees/${employeeId}/transports-track`, {
        params: { transport_id: transportId },
      }),
    transportLiveTrack: (employeeId, id) => api.get(`user/employees/${employeeId}/transports-live-track/${id}`),
    counts: (employeeId) =>
      api.get(`user/employees-bookings/${employeeId}/counts`),
  },

  // Chat APIs
  chat: {
    conversations: () => api.get("user/chats/conversations"),
    send: (data) => api.post("user/chats/send", data),
    markAsRead: () => api.post("user/chats/mark-as-read"),
  },

  // Booking APIs
  bookings: {
    index: (params = {}) => api.get("user/bookings", { params }),
    show: (id) => api.get(`user/bookings/${id}`),
    invoices: (params = {}) => api.get("user/bookings/invoices", { params }),
    calculateHome: (data) => api.post("user/bookings/calculate-home", data),
    calculateVehicles: (data) =>
      api.post("user/bookings/calculate-vehicles", data),
    store: (data) => api.post("user/bookings", data),
    sendNote: (id, data) => api.post(`user/bookings/send-note/${id}`, data),
    regular: (params = {}) => api.get("user/bookings/regular", { params }),
    fast: (params = {}) => api.get("user/bookings/fast", { params }),
    approved: (params = {}) => api.get("user/bookings/approved", { params }),
    approvedShow: (id) => api.get(`user/bookings/approved/${id}`),
    delete: (id, data) => api.post(`user/bookings/${id}/delete`, data),
    track: (bookingId) =>
      api.get("user/bookings/track", { params: { booking_id: bookingId } }),
    liveTrack: (id) => api.get(`user/bookings/live-track/${id}`),
    paymentProcess: (data) => api.post("user/bookings/payment/process", data),
    paymentCallback: (params = {}) =>
      api.get("user/bookings/payment/callback", { params }),
    paymentCallbackPost: (data) =>
      api.post("user/bookings/payment/callback", data),
  },

  // Booking Order APIs
  bookingOrders: {
    order: (id) => api.get(`user/bookings/${id}/order`),
    completed: (params = {}) => api.get("user/bookings/completed", { params }),
    regularCompleted: (params = {}) =>
      api.get("user/bookings/regular-completed", { params }),
    fastCompleted: (params = {}) =>
      api.get("user/bookings/fast-completed", { params }),
    rating: (id, data) => api.post(`user/bookings/${id}/rating`, data),
  },

  // Transport APIs
  transports: {
    index: (params = {}) => api.get("user/transports", { params }),
    show: (id) => api.get(`user/transports/${id}`),
    store: (data) => api.post("user/transports", data),
    calculateHome: (data) => api.post("user/transports/calculate-home", data),
    update: (id, data) => api.post(`user/transports/${id}`, data),
    delete: (id, data) => api.post(`user/transports/${id}/delete`, data),
    approved: (params = {}) => api.get("user/transports/approved", { params }),
    approvedShow: (id) => api.get(`user/transports/approved/${id}`),
    cars: (params = {}) => api.get("user/transports/cars", { params }),
    furnitures: (params = {}) =>
      api.get("user/transports/furnitures", { params }),
  },

  // Transport Order APIs
  transportsOrders: {
    index: (params = {}) => api.get("user/transports-orders", { params }),
    show: (id) => api.get(`user/transports-orders/${id}`),
    cars: (params = {}) => api.get("user/transports-orders/cars", { params }),
    furnitures: (params = {}) =>
      api.get("user/transports-orders/furnitures", { params }),
    byTransport: (transportId, params = {}) =>
      api.get(`user/transports/${transportId}/orders`, { params }),
    transportsChat: (params = {}) => api.get("user/transports-orders/messages", { params }),
    accept: (transportId, orderId, data) =>
      api.post(`user/transports/${transportId}/orders/${orderId}/accept`, data),
    reject: (transportId, orderId, data) =>
      api.post(`user/transports/${transportId}/orders/${orderId}/reject`, data),
    rating: (transportId, data) =>
      api.post(`user/transports/${transportId}/rating`, data),
    completedRated: (params = {}) =>
      api.get('user/transports/completed/rated', { params }),
    completedUnrated: (params = {}) =>
      api.get('user/transports/completed/unrated', { params }),
    track: (transportId) =>
      api.get("user/transports/track", {
        params: { transport_id: transportId },
      }),
    liveTrack: (id) => api.get(`user/transports/live-track/${id}`),
  },

  // Transport Order Chat APIs
  transportOrderChat: {
    getMessages: (transportId, orderId, params = {}) =>
      api.get(`user/transports/${transportId}/orders/${orderId}/messages`, {
        params,
      }),
    sendMessage: (transportId, orderId, data) =>
      api.post(
        `user/transports/${transportId}/orders/${orderId}/messages`,
        data,
      ),

    chats: (params = {}) =>
      api.get("user/transport-chats", { params }),

    makeRead: (transportId, orderId, params = {}) =>
      api.post(
        `user/transports/${transportId}/orders/${orderId}/messages/mark-read`,
        {
          params,
        },
      ),
  },

  // Profile APIs
  updateProfile: (data) => api.post("user/update-profile", data),

  // Count APIs
  counts: () => api.get("user/counts"),

  // Notification APIs
  notifications: {
    index: () => api.get("user/notifications"),
    unreadCount: () => api.get("user/notifications/unread-count"),
    markRead: (id) => api.post(`user/notifications/${id}/mark-read`),
    markAllRead: () => api.post("user/notifications/mark-all-read"),
  },
};
